<template>
  <div class="subscription-order-form">
    <el-card class="order-card">
      <div class="order-header">
        <h2>LearningFirst Platform — Subscribe for Your Students</h2>
        <p class="header-description">
          Give your students all-access to the LearningFirst Platform. Each subscription provides
          unlimited access to all courses, activities, and resources for the selected duration.
          Simply choose the subscription length and the number of student seats you need.
        </p>
      </div>

      <div class="order-body">
        <!-- Duration Selector -->
        <div class="form-section">
          <label class="section-label">Subscription Duration</label>
          <el-radio-group v-model="duration_months" @change="onFormChange" size="medium">
            <el-radio-button :label="6">6 Months</el-radio-button>
            <el-radio-button :label="12">12 Months</el-radio-button>
          </el-radio-group>
        </div>

        <!-- Quantity Input -->
        <div class="form-section">
          <label class="section-label">Number of Student Seats</label>
          <el-input-number
            v-model="quantity"
            :min="20"
            :max="1000"
            :step="10"
            size="medium"
            @change="onFormChange"
            style="width: 200px;"
          ></el-input-number>
          <span class="quantity-hint">Minimum 20 students</span>
        </div>

        <!-- Pricing Breakdown -->
        <div class="pricing-section" v-loading="pricingLoading">
          <h3 class="pricing-title">Pricing Breakdown</h3>

          <!-- Custom Pricing Message -->
          <div v-if="pricing.custom_pricing_required" class="custom-pricing-message">
            <i class="el-icon-phone-outline"></i>
            <div>
              <p class="custom-title">Contact Us for Custom Pricing</p>
              <p class="custom-desc">
                For 1,000 or more student seats, we offer custom enterprise pricing.
                Please contact our team for a tailored quote.
              </p>
            </div>
          </div>

          <!-- Normal Pricing Display -->
          <div v-else-if="pricingLoaded" class="pricing-details">
            <div class="pricing-row">
              <span class="pricing-label">School Fee Tier</span>
              <span class="pricing-value tier-badge">{{ pricing.fee_tier }} - ${{ formatPrice(pricing.base_price) }}/month base</span>
            </div>
            <div class="pricing-row">
              <span class="pricing-label">Volume Discount</span>
              <span class="pricing-value discount-badge">{{ pricing.discount_percentage }}% off ({{ pricing.volume_tier }})</span>
            </div>

            <div class="pricing-divider"></div>

            <div class="pricing-row">
              <span class="pricing-label">Per Student / Month</span>
              <span class="pricing-value">${{ formatPrice(pricing.unit_price) }}</span>
            </div>
            <div class="pricing-row">
              <span class="pricing-label">Per Student Total ({{ duration_months }} months)</span>
              <span class="pricing-value">${{ formatPrice(pricing.total_per_student) }}</span>
            </div>

            <div class="pricing-divider"></div>

            <div class="pricing-row grand-total">
              <span class="pricing-label">Grand Total ({{ quantity }} students)</span>
              <span class="pricing-value">${{ formatPrice(pricing.total_price) }}</span>
            </div>
          </div>

          <!-- Empty State -->
          <div v-else class="pricing-empty">
            <p>Select duration and quantity to see pricing.</p>
          </div>
        </div>

        <!-- Submit -->
        <div class="submit-section">
          <el-button
            type="primary"
            size="medium"
            @click="submitOrder"
            :loading="submitting"
            :disabled="!pricingLoaded || pricing.custom_pricing_required"
          >
            Place Subscription Order
          </el-button>
        </div>
      </div>
    </el-card>
  </div>
</template>

<script>
import { postRequest } from '@/api/eclub'
import { getUserId, getAccountId } from '@/utils/store'

export default {
  name: 'SubscriptionOrderForm',
  data() {
    return {
      duration_months: 6,
      quantity: 20,
      account_id: '',
      pricing: {
        fee_tier: '',
        base_price: 0,
        quantity: 0,
        duration_months: 0,
        volume_tier: '',
        discount_rate: 0,
        discount_percentage: 0,
        unit_price: 0,
        total_per_student: 0,
        total_price: 0,
        custom_pricing_required: false
      },
      pricingLoading: false,
      pricingLoaded: false,
      submitting: false,
      debounceTimer: null
    }
  },
  async created() {
    await this.fetchAccountId()
    this.fetchPricing()
  },
  beforeDestroy() {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer)
    }
  },
  methods: {
    async fetchAccountId() {
      // Try to get account_id from the store first
      const storedAccountId = getAccountId()
      if (storedAccountId) {
        this.account_id = storedAccountId
        return
      }

      // Fallback: get account_id from advisor's clubs
      try {
        const response = await postRequest('getAdvisorClubs', {
          advisor_id: getUserId()
        })
        if (response.data.code === 0 && response.data.data && response.data.data.length > 0) {
          this.account_id = response.data.data[0].account_id
        } else {
          this.$message.error('Unable to determine your school account. Please contact support.')
        }
      } catch (error) {
        console.error('Error fetching account ID:', error)
        this.$message.error('Failed to load account information')
      }
    },

    onFormChange() {
      if (this.debounceTimer) {
        clearTimeout(this.debounceTimer)
      }
      this.debounceTimer = setTimeout(() => {
        this.fetchPricing()
      }, 400)
    },

    async fetchPricing() {
      if (!this.account_id) return

      this.pricingLoading = true
      try {
        const response = await postRequest('getSubscriptionPricing', {
          account_id: this.account_id,
          quantity: this.quantity,
          duration_months: this.duration_months
        })

        if (response.data.code === 0) {
          this.pricing = response.data.data
          this.pricingLoaded = true
        } else {
          this.$message.error(response.data.message || 'Failed to fetch pricing')
        }
      } catch (error) {
        console.error('Error fetching pricing:', error)
        this.$message.error('Failed to fetch pricing information')
      } finally {
        this.pricingLoading = false
      }
    },

    formatPrice(value) {
      if (value === null || value === undefined) return '0.00'
      return Number(value).toFixed(2)
    },

    async submitOrder() {
      if (!this.account_id) {
        this.$message.warning('Account information is missing. Please refresh and try again.')
        return
      }

      if (!this.pricingLoaded) {
        this.$message.warning('Please wait for pricing to load before submitting.')
        return
      }

      try {
        this.submitting = true
        const response = await postRequest('submitSubscriptionOrder', {
          contact_id: getUserId(),
          account_id: this.account_id,
          quantity: this.quantity,
          duration_months: this.duration_months
        })

        if (response.data.code === 0) {
          const result = response.data.data
          if (result.status === 'PENDING') {
            this.$router.push({
              name: 'Subscription Payment',
              query: { order_id: result._id.$id }
            })
          } else if (result.status === 'COMPLETED') {
            this.$router.push({
              name: 'Subscription Management'
            })
          }
        } else {
          this.$message.error(response.data.message || 'Failed to submit order')
        }
      } catch (error) {
        console.error('Error submitting order:', error)
        this.$message.error('Failed to submit subscription order')
      } finally {
        this.submitting = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.subscription-order-form {
  padding: 20px;

  .order-card {
    max-width: 800px;
    margin: 0 auto;

    .order-header {
      margin-bottom: 32px;
      padding-bottom: 24px;
      border-bottom: 3px solid #FF6450;

      h2 {
        margin: 0 0 12px;
        color: #FF6450;
        font-size: 22px;
        font-weight: 600;
      }

      .header-description {
        margin: 0;
        color: #606266;
        font-size: 14px;
        line-height: 1.7;
      }
    }

    .order-body {
      .form-section {
        margin-bottom: 28px;

        .section-label {
          display: block;
          font-size: 15px;
          font-weight: 500;
          color: #303133;
          margin-bottom: 10px;
        }

        .quantity-hint {
          margin-left: 12px;
          font-size: 13px;
          color: #909399;
        }
      }

      .pricing-section {
        background: #fafafa;
        border: 1px solid #ebeef5;
        border-radius: 8px;
        padding: 24px;
        margin-bottom: 28px;
        min-height: 120px;

        .pricing-title {
          margin: 0 0 20px;
          font-size: 16px;
          color: #303133;
          font-weight: 600;
        }

        .custom-pricing-message {
          display: flex;
          align-items: flex-start;
          gap: 16px;
          padding: 20px;
          background: #fff7e6;
          border: 1px solid #ffd591;
          border-radius: 6px;

          .el-icon-phone-outline {
            font-size: 28px;
            color: #e6a23c;
            margin-top: 2px;
          }

          .custom-title {
            margin: 0 0 6px;
            font-size: 16px;
            font-weight: 600;
            color: #303133;
          }

          .custom-desc {
            margin: 0;
            font-size: 14px;
            color: #606266;
            line-height: 1.6;
          }
        }

        .pricing-details {
          .pricing-row {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 10px 0;

            .pricing-label {
              font-size: 14px;
              color: #606266;
            }

            .pricing-value {
              font-size: 14px;
              color: #303133;
              font-weight: 500;
            }

            .tier-badge {
              background: #ecf5ff;
              color: #409eff;
              padding: 3px 10px;
              border-radius: 4px;
              font-size: 13px;
            }

            .discount-badge {
              background: #f0f9eb;
              color: #67c23a;
              padding: 3px 10px;
              border-radius: 4px;
              font-size: 13px;
            }

            &.grand-total {
              .pricing-label {
                font-size: 16px;
                font-weight: 600;
                color: #303133;
              }

              .pricing-value {
                font-size: 22px;
                font-weight: 700;
                color: #FF6450;
              }
            }
          }

          .pricing-divider {
            border-top: 1px dashed #dcdfe6;
            margin: 6px 0;
          }
        }

        .pricing-empty {
          text-align: center;
          padding: 20px 0;

          p {
            margin: 0;
            color: #909399;
            font-size: 14px;
          }
        }
      }

      // ---- Subscription Duration Radio Buttons Orange Theme ----
      ::v-deep .el-radio-button__orig-radio:checked + .el-radio-button__inner {
        background-color: #FF8833;
        border-color: #FF8833;
        box-shadow: -1px 0 0 0 #FF8833;
      }

      ::v-deep .el-radio-button__inner:hover {
        color: #FF8833;
      }

      .submit-section {
        text-align: right;
        padding-top: 8px;

        .el-button {
          padding: 12px 32px;
          font-size: 16px;
        }
      }
    }
  }
}
</style>
